// Prompt 0469 — Tab Auditoria del AdminRolGobernanzaPanel.
// Lista read-only de eventos de auditoria del rol, ordenados desc por fecha.
// Cada entrada: badge de accion, usuario, fecha legible, acordeon con diff JSON.

import type React from 'react';
import { useState, useEffect } from 'react';
import {
  fetchAuditoriaTipoRolGobernanza,
  type AuditoriaEntry,
  type AccionAuditoria,
} from '../../../../../../shared/services/api';
import { useI18n } from '../../../../../../../i18n';
import { LoadingSpinner } from '../../../../../../shared/components/LoadingSpinner';

interface AuditoriaTabProps {
  tipoRolId: string;
}

const LIMIT = 50;

const ACCION_COLORS: Record<AccionAuditoria, { bg: string; text: string; label: string }> = {
  CREAR: { bg: '#ECFDF5', text: '#047857', label: 'Creado' },
  ACTUALIZAR: { bg: '#EFF6FF', text: '#1D4ED8', label: 'Actualizado' },
  ELIMINAR: { bg: '#FEF2F2', text: '#B91C1C', label: 'Eliminado' },
  APROBAR: { bg: '#ECFDF5', text: '#047857', label: 'Aprobado' },
  RECHAZAR: { bg: '#FEF2F2', text: '#B91C1C', label: 'Rechazado' },
  PUBLICAR: { bg: '#EFF6FF', text: '#1D4ED8', label: 'Publicado' },
  ARCHIVAR: { bg: '#F3F4F6', text: '#374151', label: 'Archivado' },
  EXPORTAR: { bg: '#FEF3C7', text: '#92400E', label: 'Exportado' },
  OBSOLETO: { bg: '#F3F4F6', text: '#6B7280', label: 'Obsoleto' },
  LOGIN: { bg: '#F5F3FF', text: '#6D28D9', label: 'Login' },
};

function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function AccionIcon({ accion }: { accion: AccionAuditoria }) {
  const common = {
    width: 14,
    height: 14,
    fill: 'none',
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  if (accion === 'CREAR') {
    return (
      <svg {...common} viewBox="0 0 24 24" stroke="#047857">
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
      </svg>
    );
  }
  if (accion === 'ELIMINAR') {
    return (
      <svg {...common} viewBox="0 0 24 24" stroke="#B91C1C">
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      </svg>
    );
  }
  return (
    <svg {...common} viewBox="0 0 24 24" stroke="#1D4ED8">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}

export function AuditoriaTab({ tipoRolId }: AuditoriaTabProps) {
  const { t } = useI18n();
  const adm = (t.structure as Record<string, unknown>)?.admin as
    | Record<string, unknown>
    | undefined;
  const lbl = (adm ?? {}) as Record<string, string>;
  const panel = (lbl.rolGobernanzaPanel ?? {}) as unknown as Record<string, string>;

  const [entradas, setEntradas] = useState<AuditoriaEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');
    fetchAuditoriaTipoRolGobernanza(tipoRolId, { limit: LIMIT })
      .then((res) => setEntradas(res.data ?? []))
      .catch((err) =>
        setError(err instanceof Error ? err.message : (panel.errorCargarAuditoria ?? 'Error')),
      )
      .finally(() => setLoading(false));
  }, [tipoRolId, panel.errorCargarAuditoria]);

  if (loading) return <LoadingSpinner size="sm" muted />;

  if (error) {
    return (
      <div
        style={{
          padding: '12px 16px',
          background: '#FEF2F2',
          border: '1px solid #FECACA',
          borderRadius: 6,
          fontSize: 12,
          color: '#DC2626',
        }}
      >
        {error}
      </div>
    );
  }

  if (entradas.length === 0) {
    return (
      <div
        style={{
          padding: 40,
          textAlign: 'center',
          color: '#64748B',
          fontSize: 13,
          background: '#F8FAFC',
          borderRadius: 8,
          border: '1px dashed #E2E8F0',
        }}
      >
        {panel.auditoriaVacia ?? 'Este rol aún no tiene registros de auditoría.'}
      </div>
    );
  }

  const labelFor = (accion: AccionAuditoria): string => {
    const key = `auditoriaAccion${accion.charAt(0) + accion.slice(1).toLowerCase()}`;
    return panel[key] ?? ACCION_COLORS[accion].label;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {entradas.map((e) => {
        const color = ACCION_COLORS[e.accion] ?? ACCION_COLORS.ACTUALIZAR;
        return (
          <details
            key={e.id}
            style={{
              border: '1px solid #E5E7EB',
              borderRadius: 8,
              background: '#fff',
              boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
            }}
          >
            <summary
              style={{
                listStyle: 'none',
                cursor: 'pointer',
                padding: '10px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                fontSize: 12,
              }}
            >
              <AccionIcon accion={e.accion} />
              <span
                style={{
                  display: 'inline-block',
                  padding: '2px 8px',
                  borderRadius: 10,
                  fontSize: 11,
                  fontWeight: 700,
                  background: color.bg,
                  color: color.text,
                  letterSpacing: 0.3,
                }}
              >
                {labelFor(e.accion)}
              </span>
              <span style={{ color: '#0F172A', fontWeight: 600 }}>
                {e.usuario?.nombreCompleto ??
                  panel.auditoriaUsuarioDesconocido ??
                  'Usuario desconocido'}
              </span>
              <span
                style={{ color: '#94A3B8', marginLeft: 'auto', fontVariantNumeric: 'tabular-nums' }}
              >
                {formatDate(e.creadoEn)}
              </span>
            </summary>
            <div
              style={{
                padding: '12px 16px',
                borderTop: '1px solid #F1F5F9',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 12,
              }}
            >
              {e.accion !== 'CREAR' && (
                <div>
                  <p style={diffLabelStyle}>
                    {panel.auditoriaDatosAnteriores ?? 'Datos anteriores'}
                  </p>
                  <pre style={preStyle}>{jsonPretty(e.datosAnteriores)}</pre>
                </div>
              )}
              {e.accion !== 'ELIMINAR' && (
                <div style={{ gridColumn: e.accion === 'CREAR' ? '1 / -1' : undefined }}>
                  <p style={diffLabelStyle}>{panel.auditoriaDatosNuevos ?? 'Datos nuevos'}</p>
                  <pre style={preStyle}>{jsonPretty(e.datosNuevos)}</pre>
                </div>
              )}
            </div>
          </details>
        );
      })}
      {entradas.length >= LIMIT && (
        <p
          style={{
            marginTop: 6,
            fontSize: 11,
            color: '#94A3B8',
            textAlign: 'center',
            fontStyle: 'italic',
          }}
        >
          {panel.auditoriaLimiteNota ??
            `Mostrando las últimas ${LIMIT} entradas. Acceso completo desde Compliance.`}
        </p>
      )}
    </div>
  );
}

function jsonPretty(v: unknown): string {
  if (v === null || v === undefined) return '—';
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

const diffLabelStyle: React.CSSProperties = {
  margin: '0 0 4px',
  fontSize: 10,
  fontWeight: 700,
  color: '#64748B',
  textTransform: 'uppercase',
  letterSpacing: 0.4,
};

const preStyle: React.CSSProperties = {
  margin: 0,
  padding: '8px 10px',
  background: '#F8FAFC',
  border: '1px solid #E2E8F0',
  borderRadius: 6,
  fontSize: 11,
  lineHeight: 1.4,
  color: '#334155',
  fontFamily: "'JetBrains Mono', monospace",
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  maxHeight: 220,
  overflow: 'auto',
};
